import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { CheckCircle, XCircle } from '@phosphor-icons/react';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: 'polling', tries: 0 });
  const timeoutRef = useRef();

  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) { navigate('/credits'); return; }

    const poll = async (tries = 0) => {
      if (tries >= 30) { setState({ status: 'timeout' }); return; }
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === 'paid') {
          await refresh();
          setState({ status: 'paid' });
          setTimeout(() => navigate('/credits'), 2500);
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'failed') {
          setState({ status: 'failed' }); return;
        }
      } catch (e) { /* keep polling */ }
      timeoutRef.current = setTimeout(() => poll(tries + 1), 2000);
    };
    poll();
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div className="border border-zinc-800 rounded-xl bg-zinc-900/60 p-10 max-w-md w-full text-center">
        {state.status === 'polling' && (
          <>
            <div className="w-14 h-14 rounded-full border-2 border-[#ccff00] border-t-transparent animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl">Confirming payment…</h1>
            <p className="font-mono text-xs text-zinc-500 mt-2">One moment — Stripe is telling us the good news.</p>
          </>
        )}
        {state.status === 'paid' && (
          <>
            <CheckCircle size={64} weight="fill" className="text-[#ccff00] mx-auto mb-4" />
            <h1 className="font-display text-3xl">You're topped up.</h1>
            <p className="text-sm text-zinc-400 mt-2">Redirecting you back to your wallet…</p>
          </>
        )}
        {(state.status === 'failed' || state.status === 'timeout') && (
          <>
            <XCircle size={64} className="text-red-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl">Payment not confirmed</h1>
            <p className="text-sm text-zinc-400 mt-2">Something went wrong or timed out. If you were charged, credits will appear shortly.</p>
            <button data-testid="back-to-credits" onClick={() => navigate('/credits')} className="mt-6 btn-neon px-5 py-2 rounded-full font-display font-semibold">Back to Credits</button>
          </>
        )}
      </div>
    </div>
  );
}
